import { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { createArabicMap } from '../../utils/mapbox';
import * as turf from '@turf/turf';

const BAGHDAD_CENTER: [number, number] = [44.3661, 33.3152]; // [lng, lat]
const BAGHDAD_RADIUS_KM = 100;

interface MapPickerProps {
  lat: number | null;
  lng: number | null;
  onLocationChange: (lat: number, lng: number) => void;
  height?: string;
}

export default function MapPicker({ lat, lng, onLocationChange, height = '300px' }: MapPickerProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markerRef = useRef<mapboxgl.Marker | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || mapRef.current) return;

    const map = createArabicMap(mapContainer.current, {
      center: lat && lng ? [lng, lat] : BAGHDAD_CENTER,
      zoom: lat && lng ? 14 : 11,
    });

    map.addControl(new mapboxgl.NavigationControl(), 'top-right');

    map.on('load', () => {
      // Draw Baghdad radius circle
      const circle = turf.circle(BAGHDAD_CENTER, BAGHDAD_RADIUS_KM, { units: 'kilometers' });
      map.addSource('baghdad-radius', {
        type: 'geojson',
        data: circle,
      });
      map.addLayer({
        id: 'baghdad-radius-fill',
        type: 'fill',
        source: 'baghdad-radius',
        paint: {
          'fill-color': '#3b82f6',
          'fill-opacity': 0.05,
        },
      });
      map.addLayer({
        id: 'baghdad-radius-line',
        type: 'line',
        source: 'baghdad-radius',
        paint: {
          'line-color': '#3b82f6',
          'line-width': 1.5,
          'line-dasharray': [3, 2],
        },
      });

      setMapLoaded(true);
    });

    // Click to place marker
    map.on('click', (e) => {
      onLocationChange(e.lngLat.lat, e.lngLat.lng);
    });

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update marker when lat/lng changes
  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return;

    if (lat !== null && lng !== null) {
      if (markerRef.current) {
        markerRef.current.setLngLat([lng, lat]);
      } else {
        markerRef.current = new mapboxgl.Marker({ color: '#8b5cf6', draggable: true })
          .setLngLat([lng, lat])
          .addTo(mapRef.current);

        markerRef.current.on('dragend', () => {
          const lngLat = markerRef.current!.getLngLat();
          onLocationChange(lngLat.lat, lngLat.lng);
        });
      }
    } else {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
    }
  }, [lat, lng, mapLoaded, onLocationChange]);

  return (
    <div
      ref={mapContainer}
      style={{ height, width: '100%' }}
      className="rounded-lg border border-gray-200 overflow-hidden"
    />
  );
}
